import {useLazyQuery} from '@apollo/client'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {Col, Row, Spinner, Card} from 'react-bootstrap'
import {GET_TOTAL_LEADS} from '../../../gql/queries/reportsQuery'
import {IResTotalLeads} from '../../../types/reportsTypes'
import {parseId} from '../../../helpers'
import {KTIcon} from '../../../../../_metronic/helpers'

interface LeadTotalsKpiProps {
  pipelineId: string
  dates: Date[]
}

const toDateParam = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const LeadTotalsKpi: React.FC<LeadTotalsKpiProps> = ({pipelineId, dates}) => {
  const [
    gqlGetMonthTotal,
    {data: dataMonth, loading: loadingMonth, error: errorMonth},
  ] = useLazyQuery<IResTotalLeads>(GET_TOTAL_LEADS, {fetchPolicy: 'no-cache'})

  const [
    gqlGetTodayTotal,
    {data: dataToday, loading: loadingToday, error: errorToday},
  ] = useLazyQuery<IResTotalLeads>(GET_TOTAL_LEADS, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    if (!dates || dates.length <= 1) return
    const today = new Date()
    const pipelineParam = pipelineId ? parseId(pipelineId) : undefined
    const [startDate, endDate] = dates

    gqlGetMonthTotal({
      variables: {
        ...(pipelineParam ? {pipelineId: pipelineParam} : {}),
        startDate: toDateParam(startDate),
        endDate: toDateParam(endDate),
      },
    })
    gqlGetTodayTotal({
      variables: {
        ...(pipelineParam ? {pipelineId: pipelineParam} : {}),
        startDate: toDateParam(today),
        endDate: toDateParam(today),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, dates])

  useEffect(() => {
    if (errorMonth) toast.error(`Error: ${errorMonth.message}`)
    if (errorToday) toast.error(`Error: ${errorToday.message}`)
  }, [errorMonth, errorToday])

  const monthTotal = dataMonth?.totalLeads ?? 0
  const todayTotal = dataToday?.totalLeads ?? 0

  const isLoadingMonth = loadingMonth || !dataMonth
  const isLoadingToday = loadingToday || !dataToday

  return (
    <>
      <Col sm={12} md={6} lg={2}>
          <Card className='card-flush h-100'>
            <Card.Body className='d-flex flex-column'>
              <div className='d-flex align-items-center mb-2'>
                <KTIcon iconName='calendar' className='fs-2x text-success me-2' />
              <span className='text-gray-700 fw-bold fs-6'>Leads in Date Range</span>
              </div>
              <div className='d-flex align-items-baseline'>
                <span className='fs-2hx fw-bold text-dark me-2'>
                  {isLoadingMonth ? <Spinner animation='border' size='sm' /> : monthTotal}
                </span>
                <span className='text-muted fs-7'>This Period</span>
              </div>
              <div className='mt-2'>
                <span className='text-muted fs-8'>Selected range</span>
              </div>
            </Card.Body>
          </Card>
      </Col>
      <Col sm={12} md={6} lg={2}>
        <Card className='card-flush h-100'>
          <Card.Body className='d-flex flex-column'>
            <div className='d-flex align-items-center mb-2'>
              <KTIcon iconName='calendar-8' className='fs-2x text-info me-2' />
              <span className='text-gray-700 fw-bold fs-6'>Leads Today</span>
            </div>
            <div className='d-flex align-items-baseline'>
              <span className='fs-2hx fw-bold text-dark me-2'>
                {isLoadingToday ? <Spinner animation='border' size='sm' /> : todayTotal}
              </span>
              <span className='text-muted fs-7'>Today</span>
            </div>
            <div className='mt-2'>
                <span className='text-muted fs-8'>Today total</span>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </>
  )
}
